declare let mxPoint: any;
declare let mxUtils: any;
import * as _ from 'lodash';
import {Injectable} from '@angular/core';
import {GraphHelperService} from './graph-helper.service';
import {EditorRegistryService} from '../mx-graph/editor/editor-registry.service';
import {isActivity, isConnector, isTerminator} from './shapes-detectors.util';
import {translateEdgesUtil} from './translate-edges.util';
import {xmlToObj} from './xml-to-obj.util';
import {setConnectionPoints} from './setConnectionPoints';

@Injectable()
export class FlowlineHelperService {
  constructor(
    private registry: EditorRegistryService,
    private graphHelper: GraphHelperService
  ) {}

  translateConnectedEdges(cells, moving, resizing?) {
    const graph = this.registry.getEditorUI().editor.graph;

    graph.model.beginUpdate();
    const edges = translateEdgesUtil(graph, cells, moving, resizing);

    this.optimizeEdges(edges.srcAlignEdges, true);
    this.optimizeEdges(edges.tgtAlignEdges, false);
    graph.model.endUpdate();
  }

  setWps(points, delta, isSource, horizontal) {
    const pos = isSource ? 0 : points.length - 1;
    const coord = horizontal ? 'x' : 'y';
    const wp = points[pos];

    wp[coord] = wp[coord] + delta;
    points[pos] = wp;

    return points;
  }

  getSmartWp(edge, constraint, points, cell, isSource) {
    const graph = this.registry.getEditorUI().editor.graph;
    const oldStyle = graph.getCellStyle(edge);
    const newStyle = this.getClosestSide(constraint.point.x, constraint.point.y);
    const newX = typeof newStyle.x === 'undefined' ? null : newStyle.x;
    const newY = typeof newStyle.y === 'undefined' ? null : newStyle.y;
    let side, deltaX, deltaY;
    let newPoints = _.cloneDeep(points);

    if (!isSource) {
      const oldEntryDir = this.eDirection(oldStyle.entryX, oldStyle.entryY);
      const newEntryDir = this.eDirection(newX, newY);

      if (oldEntryDir !== newEntryDir) {
        newPoints = null;
      } else {
        side = oldEntryDir === 'south' || oldEntryDir === 'north';

        if (side && newPoints && newPoints.length) {
          deltaX = (newStyle.x - oldStyle.entryX) * cell.geometry.width;
          newPoints = this.setWps(newPoints, deltaX, false, true);
        } else if (!side && newPoints && newPoints.length) {
          deltaY = (newStyle.y - oldStyle.entryY) * cell.geometry.height;
          newPoints = this.setWps(newPoints, deltaY, false, false);
        }
      }
    } else {
      const oldExitDir = this.eDirection(oldStyle.exitX, oldStyle.exitY);
      const newExitDir = this.eDirection(newX, newY);

      if (oldExitDir !== newExitDir) {
        newPoints = null;
      } else {
        side = oldExitDir === 'south' || oldExitDir === 'north';

        if (side && newPoints && newPoints.length) {
          deltaX = (newStyle.x - oldStyle.exitX) * cell.geometry.width;
          newPoints = this.setWps(newPoints, deltaX, true, true);
        } else if (!side && newPoints && newPoints.length) {
          deltaY = (newStyle.y - oldStyle.exitY) * cell.geometry.height;
          newPoints = this.setWps(newPoints, deltaY, true, false);
        }
      }
    }

    return newPoints;
  }

  /** Returns position object including offset for start or end line texts,
   * considering the orientation of the line
   * edge - edge cell
   * isStart - boolean indicating if calculation requested for start or end text
   * */
  getSmartLineTextPosition(edge, isStart) {
    const position = {
      x: isStart ? -1 : 1, // 0 is the middle of the line
      y: 0,
      offset: {
        x: 0,
        y: 0
      }
    };

    const graph = this.registry.getEditorUI().editor.graph;
    const state = graph.view.getState(edge);
    // we use state points here because we want to know how line is actually displayed
    // and waypoints from geometry cannot tell us this
    const points = state.absolutePoints;
    const [p1, p2] = isStart ? points : points.slice(-2);

    const isHorizontal = Math.abs(p2.y - p1.y) < 3;

    const l2r = isHorizontal && p2.x > p1.x;
    const r2l = isHorizontal && p2.x < p1.x;
    const t2b = !isHorizontal && p2.y > p1.y;
    const b2t = !isHorizontal && p2.y < p1.y;

    if (l2r) {
      position.offset.x = isStart ? 40 : -40;
      position.offset.y = -15;
    }

    if (r2l) {
      position.offset.x = isStart ? -40 : 40;
      position.offset.y = -15;
    }

    if (t2b) {
      position.offset.x = -40;
      position.offset.y = isStart ? 15 : -15;
    }

    if (b2t) {
      position.offset.x = 40;
      position.offset.y = isStart ? -15 : 15;
    }

    return position;
  }

  /** Returns the string meaning from which side flowline is "growing"
   * X, Y - exit or entry points of flowline (take if from edge style)
   * */
  eDirection(X, Y) {
    let direction;

    if (X !== 0 && X !== 1) {
      direction = Y > 0 ? 'south' : 'north';
    }

    if (Y !== 0 && Y !== 1) {
      direction = X > 0 ? 'east' : 'west';
    }

    return direction;
  }

  /**
   * Calculates the connection entry/exit point according to the current
   * coordinates of the mouse pointer inside of activity
   */
  getClosestSide(x, y): any {
    let deltaX, deltaY, ew, ns;
    const result: any = {};

    if (x <= 0.5) {
      ew = 'west';
      deltaX = Math.abs(0 - x);
    } else {
      ew = 'east';
      deltaX = Math.abs(1 - x);
    }

    if (y <= 0.5) {
      ns = 'north';
      deltaY = Math.abs(0 - y);
    } else {
      ns = 'south';
      deltaY = Math.abs(1 - y);
    }

    result.x = deltaY < deltaX ? x : ew === 'west' ? 0 : 1;
    result.y = deltaX < deltaY ? y : ns === 'north' ? 0 : 1;

    if (+result.x === 0 || +result.x === 1) {
      if (result.y >= 0.99) {
        result.y = 0.99;
      } else if (result.y <= 0.01) {
        result.y = 0.01;
      }
    }

    if (+result.y === 0 || +result.y === 1) {
      if (result.x >= 0.99) {
        result.x = 0.99;
      } else if (result.x <= 0.01) {
        result.x = 0.01;
      }
    }

    return result;
  }

  findOldStyle(styleChanges) {
    const oldStyle = {
      entryX: null,
      entryY: null,
      exitX: null,
      exitY: null
    };
    const keys = ['entryX', 'entryY', 'exitX', 'exitY'];

    styleChanges.forEach(function (item) {
      const parsed = xmlToObj(item.previous);

      keys.forEach(function (key) {
        if (oldStyle[key] == null) {
          if (Object.keys(parsed).indexOf(key)) {
            oldStyle[key] = parsed[key];
          }
        }
      });
    });

    return oldStyle;
  }

  getPointsBySide(side) {
    switch (side) {
      case 'north':
        return [0.5, 0];

      case 'west':
        return [0, 0.5];

      case 'east':
        return [1, 0.5];

      case 'south':
        return [0.5, 1];
    }
  }

  setConnectionSide(mxKey, side, terminal, isSource) {
    const graph = this.registry.getEditorUI().editor.graph;
    const model = graph.getModel();
    const prefix = isSource ? 'exit' : 'entry';
    const points = this.getPointsBySide(side);

    model.beginUpdate();
    const flowlineCell = model.getCell(+mxKey);
    let geo = graph.getCellGeometry(flowlineCell);

    if (geo != null) {
      geo = geo.clone();
      geo.points = null;
      model.setGeometry(flowlineCell, geo);
    }

    model.setTerminal(flowlineCell, terminal, isSource);
    graph.setCellStyles(prefix + 'X', points[0], [flowlineCell]);
    graph.setCellStyles(prefix + 'Y', points[1], [flowlineCell]);
    model.endUpdate();
  }

  reverseLine(cell) {
    if (!cell.edge) {
      return;
    }

    const graph = this.registry.getEditorUI().editor.graph;
    const model = graph.getModel();
    let geo = graph.getCellGeometry(cell);
    const sourceData: {cell?; exitX?; exitY?; srcPoint?} = {};
    const targetData: {cell?; entryX?; entryY?; tgtPoint?} = {};

    if (cell.source) {
      sourceData.cell = cell.source;
      sourceData.exitX = graph.getCellStyle(cell).exitX;
      sourceData.exitY = graph.getCellStyle(cell).exitY;
    } else {
      sourceData.cell = null;
      sourceData.srcPoint = geo.sourcePoint;
    }

    if (cell.target) {
      targetData.cell = cell.target;
      targetData.entryX = graph.getCellStyle(cell).entryX;
      targetData.entryY = graph.getCellStyle(cell).entryY;
    } else {
      targetData.cell = null;
      targetData.tgtPoint = geo.targetPoint;
    }

    model.beginUpdate();
    geo = geo.clone();

    if (geo.points && geo.points.length > 0) {
      geo.points.reverse();
    }

    if (sourceData.cell) {
      model.setTerminal(cell, sourceData.cell, false);
      graph.setCellStyles('entryX', sourceData.exitX, [cell]);
      graph.setCellStyles('entryY', sourceData.exitY, [cell]);
    } else {
      model.setTerminal(cell, null, false);
      geo.setTerminalPoint(sourceData.srcPoint, false);
    }

    if (targetData.cell) {
      model.setTerminal(cell, targetData.cell, true);
      graph.setCellStyles('exitX', targetData.entryX, [cell]);
      graph.setCellStyles('exitY', targetData.entryY, [cell]);
    } else {
      model.setTerminal(cell, null, true);
      geo.setTerminalPoint(targetData.tgtPoint, true);
    }

    model.setGeometry(cell, geo);
    model.endUpdate();
  }

  optimizeEdges(edges, alignViaTgt) {
    edges = edges || this.graphHelper.allFlowlines();
    const graph = this.registry.getEditorUI().editor.graph;

    graph.model.beginUpdate();
    edges.forEach(edge => {
      if (edge.keepStraight) {
        this.makeStraight(edge, false, alignViaTgt);
        edge.keepStraight = false;
      }
    });
    graph.model.endUpdate();
  }

  makeStraight(cell, updateModel?, alignViaTgt?) {
    if (!cell.edge) {
      return;
    }

    if ((cell.source && !cell.target) || (cell.target && !cell.source)) {
      return this.straightLineWithOneTerminal(cell, updateModel);
    }

    if (cell.source && cell.target && +cell.source.id !== +cell.target.id) {
      return this.straightLineWithTwoTerminals(
        cell,
        cell.source,
        cell.target,
        updateModel,
        alignViaTgt
      );
    }
  }

  analyzeCellsLines(cells, skipStraight = false) {
    const graph = this.registry.getEditorUI().editor.graph;
    const size = cells.length;

    if (skipStraight) {
      return;
    }

    for (let i = 0; i < size; i++) {
      const cell = cells[i];

      if (!isActivity(cell) || !cell.edges) {
        continue;
      }

      for (let j = 0; j < cell.edges.length; j++) {
        const edge = cell.edges[j];

        if (!edge.source || !edge.target) {
          continue;
        }

        const style = graph.getCellStyle(edge);
        const route =
          this.eDirection(style.exitX, style.exitY) + this.eDirection(style.entryX, style.entryY);
        const srcPoint = this.calculateAbsPoint(edge.source, style, true);
        const tgtPoint = this.calculateAbsPoint(edge.target, style, false);
        const points = edge.geometry.points || [];

        switch (route) {
          case 'eastwest':

          case 'westeast':
            edge.keepStraight =
              Math.abs(srcPoint.y - tgtPoint.y) <= 15 &&
              this.checkWaypointsLevel(points, 'y', srcPoint.y);
            break;

          case 'northsouth':

          case 'southnorth':
            edge.keepStraight =
              Math.abs(srcPoint.x - tgtPoint.x) <= 15 &&
              this.checkWaypointsLevel(points, 'x', srcPoint.x);
            break;
        }
      }
    }
  }

  setConnectionPoints(cell) {
    const graph = this.registry.getEditorUI().editor.graph;

    return setConnectionPoints(cell, graph);
  }

  adjustInteriorTerminalPoint(cell, x, y, isSource?) {
    const prefix = isSource ? 'exit' : 'entry';

    if (x >= y) {
      if (1 - x >= y) {
        // A
        y = 0;
      } else {
        // B
        x = 1;
      }
    } else {
      if (1 - x >= y) {
        // D
        x = 0;
      } else {
        // C
        y = 1;
      }
    }

    cell.style = mxUtils.setStyle(cell.style, prefix + 'X', x);
    cell.style = mxUtils.setStyle(cell.style, prefix + 'Y', y);
  }

  private straightLineWithOneTerminal(edge, updateModel) {
    const graph = this.registry.getEditorUI().editor.graph;
    const style = graph.getCellStyle(edge);
    const geo = edge.geometry.clone();
    const isSource = !!edge.source;
    const terminal = edge.source || edge.target;
    const direction = isSource
      ? this.eDirection(style.exitX, style.exitY)
      : this.eDirection(style.entryX, style.entryY);
    const floatPoint = geo.getTerminalPoint(!isSource);
    const terminalPoint = this.calculateAbsPoint(terminal, style, isSource);
    let canMakeStraight = false;

    switch (direction) {
      case 'east':
        canMakeStraight = floatPoint.x > terminalPoint.x + 15;
        floatPoint.y = terminalPoint.y;
        break;

      case 'west':
        canMakeStraight = floatPoint.x < terminalPoint.x - 15;
        floatPoint.y = terminalPoint.y;
        break;

      case 'north':
        canMakeStraight = floatPoint.y < terminalPoint.y - 15;
        floatPoint.x = terminalPoint.x;
        break;

      case 'south':
        canMakeStraight = floatPoint.y > terminalPoint.y + 15;
        floatPoint.x = terminalPoint.x;
        break;
    }

    if (!canMakeStraight) {
      return;
    }

    geo.setTerminalPoint(floatPoint, !isSource);
    geo.points = null;

    if (updateModel) {
      graph.model.beginUpdate();
    }

    graph.model.setGeometry(edge, geo);

    if (updateModel) {
      graph.model.endUpdate();
    }
  }

  private calculateAbsPoint(terminal, style, isSource) {
    const geo = terminal.geometry;
    const xConnection = isSource ? style.exitX : style.entryX;
    const yConnection = isSource ? style.exitY : style.entryY;
    const absX = geo.width * xConnection + geo.x;
    const absY = geo.height * yConnection + geo.y;

    return new mxPoint(absX, absY);
  }

  private straightLineWithTwoTerminals(edge, srcCell, tgtCell, updateModel, alignViaTgt) {
    const graph = this.registry.getEditorUI().editor.graph;
    const style = graph.getCellStyle(edge);
    const geo = edge.geometry.clone();

    const srcDir = this.eDirection(style.exitX, style.exitY);
    const tgtDir = this.eDirection(style.entryX, style.entryY);
    let route = srcDir + tgtDir;

    const srcPoint = this.calculateAbsPoint(srcCell, style, true);
    const tgtPoint = this.calculateAbsPoint(tgtCell, style, false);

    switch (route) {
      case 'eastwest':
        route = srcPoint.x < tgtPoint.x ? 'eastwest' : '';
        break;

      case 'westeast':
        route = srcPoint.x > tgtPoint.x ? 'westeast' : '';
        break;

      case 'northsouth':
        route = srcPoint.y > tgtPoint.y ? 'northsouth' : '';
        break;

      case 'southnorth':
        route = srcPoint.y < tgtPoint.y ? 'southnorth' : '';
        break;

      default:
        route = '';
    }

    if (
      isTerminator(srcCell) ||
      isTerminator(tgtCell) ||
      isConnector(srcCell) ||
      isConnector(tgtCell)
    ) {
      const movement = this.findSuitableTerminatorPosition(
        route,
        srcPoint,
        srcCell,
        tgtPoint,
        tgtCell
      );

      if (!movement) {
        return;
      }

      const terCon = isTerminator(srcCell) || isConnector(srcCell) ? srcCell : tgtCell;
      const newTerConGeo = terCon.geometry.clone();

      newTerConGeo.x -= movement.dx;
      newTerConGeo.y -= movement.dy;

      if (updateModel) {
        graph.model.beginUpdate();
      }

      geo.points = null;
      graph.model.setGeometry(edge, geo);
      graph.model.setGeometry(terCon, newTerConGeo);

      if (updateModel) {
        graph.model.endUpdate();
      }

      return;
    }

    const suitable = this.findSuitableConnectionPoints(
      route,
      srcPoint,
      srcCell,
      tgtPoint,
      tgtCell,
      alignViaTgt
    );

    if (!suitable) {
      return;
    }

    let styleString = edge.style;

    Object.keys(suitable).forEach(function (key) {
      styleString = mxUtils.setStyle(styleString, key, suitable[key]);
    });

    geo.points = null;

    if (updateModel) {
      graph.model.beginUpdate();
    }

    graph.model.setGeometry(edge, geo);
    graph.model.setStyle(edge, styleString);

    if (updateModel) {
      graph.model.endUpdate();
    }
  }

  private findSuitableTerminatorPosition(route, srcPoint, srcCell, tgtPoint, tgtCell) {
    if (!route) {
      return;
    }

    const moveSrc = isTerminator(srcCell) || isConnector(srcCell);
    const movedCellGeo = moveSrc ? srcCell.geometry : tgtCell.geometry;
    const comparePoint = moveSrc ? tgtPoint : srcPoint;

    switch (route) {
      case 'eastwest':

      case 'westeast':
        return {
          dx: 0,
          dy: Math.round(movedCellGeo.y + movedCellGeo.height / 2 - comparePoint.y)
        };

      case 'northsouth':

      case 'southnorth':
        return {
          dx: Math.round(movedCellGeo.x + movedCellGeo.width / 2 - comparePoint.x),
          dy: 0
        };

      default:
        return null;
    }
  }

  private findSuitableConnectionPoints(route, srcPoint, srcCell, tgtPoint, tgtCell, alignViaTgt) {
    if (!route) {
      return;
    }

    let straightBySrc, straightByTgt, straightByCommonRange;

    switch (route) {
      case 'eastwest':

      case 'westeast':
        straightBySrc = this.findPossiblePointOnSide(srcPoint.y, tgtCell.geometry, true);

        straightByTgt = this.findPossiblePointOnSide(tgtPoint.y, srcCell.geometry, true);

        if (alignViaTgt && straightByTgt) {
          return {exitY: straightByTgt};
        }

        if (straightBySrc) {
          return {entryY: straightBySrc};
        }

        if (straightByTgt) {
          return {exitY: straightByTgt};
        }

        straightByCommonRange = this.findCommonRange(srcCell.geometry, tgtCell.geometry, true);

        if (straightByCommonRange) {
          return straightByCommonRange;
        }

        break;

      case 'northsouth':

      case 'southnorth':
        straightBySrc = this.findPossiblePointOnSide(srcPoint.x, tgtCell.geometry, false);

        straightByTgt = this.findPossiblePointOnSide(tgtPoint.x, srcCell.geometry, false);

        if (alignViaTgt && straightByTgt) {
          return {exitX: straightByTgt};
        }

        if (straightBySrc) {
          return {entryX: straightBySrc};
        }

        if (straightByTgt) {
          return {exitX: straightByTgt};
        }

        straightByCommonRange = this.findCommonRange(srcCell.geometry, tgtCell.geometry, false);

        if (straightByCommonRange) {
          return straightByCommonRange;
        }

        break;

      default:
        return null;
    }

    return null;
  }

  private findPossiblePointOnSide(pos, geo, vertical) {
    const seg = vertical ? geo.height : geo.width;
    const start = vertical ? geo.y : geo.x;
    const range = [];

    range[0] = Math.ceil(start + seg * 0.01);
    range[1] = Math.floor(start + seg * 0.99);
    const possible = pos >= range[0] && pos <= range[1];

    if (!possible) {
      return null;
    }

    return Math.round((10000 * (pos - start)) / seg) / 10000;
  }

  private findCommonRange(geo1, geo2, vertical) {
    const range1 = vertical ? [geo1.y, geo1.y + geo1.height] : [geo1.x, geo1.x + geo1.width];
    const range2 = vertical ? [geo2.y, geo2.y + geo2.height] : [geo2.x, geo2.x + geo2.width];
    const commonRange = [Math.max(range1[0], range2[0]), Math.min(range1[1], range2[1])];

    if (commonRange[0] >= commonRange[1]) {
      return null;
    }

    const pos = (commonRange[0] + commonRange[1]) / 2;
    const keys = vertical ? ['exitY', 'entryY'] : ['exitX', 'entryX'];
    const result = {};

    result[keys[0]] = this.findPossiblePointOnSide(pos, geo1, vertical);
    result[keys[1]] = this.findPossiblePointOnSide(pos, geo2, vertical);

    if (!result[keys[0]] || !result[keys[1]]) {
      return null;
    }

    return result;
  }

  private checkWaypointsLevel(points, coord, position) {
    let result = true;

    for (let i = 0; i < points.length; i++) {
      if (Math.abs(points[i][coord] - position) >= 15) {
        result = false;
        break;
      }
    }

    return result;
  }
}
